import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { CustomerResponse } from '../../types';
import OutputGraph from '../../pages/dashboard_pages/OutputGraph';
import TransformationRoadmapChart from './TransformationRoadmapChart';
import TransformationMaturityGraph from '../../pages/dashboard_pages/TransformationMaturityGraph';
import { ArrowDownTrayIcon, DocumentArrowDownIcon } from '@heroicons/react/24/outline';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import { PRESENTATION_MATURITY_PLANS } from '../../constants/maturity-plans';
import { useDemoPresentationData } from '../../hooks/useDemoPresentationData';
import Docxtemplater from 'docxtemplater';
import PizZip from 'pizzip';
import PizZipUtils from 'pizzip/utils/index.js';
import { saveAs } from 'file-saver';
import expressionParser from 'docxtemplater/expressions';
import ImageModule from 'docxtemplater-image-module-free';

interface CustomerDemoPresentationProps {
  customerData: CustomerResponse | undefined;
  isOpen: boolean;
  onClose: () => void;
}

type SlideType = 'customer-overview' | 'project-output' | 'project-output-modernization' | 'maturity-graph' | 'transformation-roadmap';

interface PresentationSlide {
  id: string;
  title: string;
  description: string;
  slideType: SlideType;
  maturityPlanId?: string;
  maturityPlanName?: string;
  component: React.ReactNode;
}

const DEMO_OUTPUT_MAP_MIN_WIDTH = 1100;
const DEMO_OUTPUT_MAP_MIN_HEIGHT = 420;
const DEMO_OUTPUT_MAP_MAX_HEIGHT = 460;
const DEMO_OUTPUT_MAP_HEIGHT_RATIO = 0.36;

const CustomerDemoPresentation: React.FC<CustomerDemoPresentationProps> = ({
  customerData,
  isOpen,
  onClose
}) => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isDownloadingPng, setIsDownloadingPng] = useState(false);
  const presentationRef = useRef<HTMLDivElement>(null);
  const downloadPngRef = useRef<(() => Promise<void>) | null>(null);

  const { roadmapData } = useDemoPresentationData(customerData);

  const projectOutputFlags = useMemo(() => {
    const projects = customerData?.top_10_projects?.top_projects ?? [];
    const isModernization = (p: any) =>
      p?.modernization_plan_project === 'Yes' || p?.Modernization_plan_project === 'Yes';
    return {
      hasModernizationProjects: projects.some(isModernization),
      hasNonModernizationProjects: projects.some((p: any) => !isModernization(p))
    };
  }, [customerData?.top_10_projects?.top_projects]);

  // Build presentation slides dynamically
  const slides: PresentationSlide[] = useMemo(() => {
    const downloadButton = (
      <button
        data-download-png
        onClick={() => downloadPngRef.current?.()}
        disabled={isDownloadingPng}
        className="inline-flex items-center shrink-0 px-4 py-2 bg-gray-600 text-white text-sm font-medium rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-w-[140px]"
      >
        <ArrowDownTrayIcon className="w-4 h-4 mr-2 shrink-0" />
        {isDownloadingPng ? 'Downloading...' : 'Download as PNG'}
      </button>
    );
    const baseSlides: PresentationSlide[] = [
    {
      id: 'customer-overview',
      title: 'Customer Overview',
      description: 'Key customer information and profile',
      slideType: 'customer-overview',
      component: (
        <div className="p-6 presentation-slide">
          <div className="max-w-7xl mx-auto">
            <div className="bg-white rounded-xl shadow-lg p-6 flex flex-col">
              {/* Removed duplicate header - only keep the slide title */}
              
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 flex-1">
                {/* Left Column - Client Details (Large Card) */}
                <div className="bg-white rounded-lg p-6 border border-gray-200">
                  <h3 className="text-lg font-semibold text-gray-900 mb-4">Client Details</h3>
                  <div className="space-y-4">
                    <div>
                      <span className="font-semibold text-gray-900">Company Name: </span>
                      <span className="text-gray-700">{customerData?.customer_details?.name || 'Company Name'}</span>
                    </div>
                    <div>
                      <span className="font-semibold text-gray-900">Engagement Type: </span>
                      <span className="text-gray-700">{customerData?.customer_details?.industry_type || 'Industry Type'}</span>
                    </div>
                    <div>
                      <span className="font-semibold text-gray-900">Company Profile: </span>
                      <p className="text-gray-700 mt-1 leading-relaxed">
                        {customerData?.customer_details?.company_profile || 'No company profile available.'}
                      </p>
                    </div>
                    <div>
                      <span className="font-semibold text-gray-900">Our Relationship: </span>
                      <p className="text-gray-700 mt-1 leading-relaxed">
                        {customerData?.customer_details?.relationship_summary || 'No relationship summary available.'}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Right Column - Three Stacked Cards */}
                <div className="space-y-4">
                  {/* Key Goals */}
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="text-md font-semibold text-gray-900">Key Goals</h4>
                      {/* Hide See All button in presentation but keep functionality */}
                      <span className="text-blue-600 text-sm cursor-pointer presentation-hide">See All →</span>
                    </div>
                    <ul className="space-y-2">
                      {customerData?.goals?.key_goals && customerData.goals.key_goals.length > 0 ? (
                        customerData.goals.key_goals.slice(0, 3).map((goal, index) => (
                          <li key={index} className="flex items-start space-x-2">
                            <span className="text-yellow-500 mt-1">⚡</span>
                            <span className="text-gray-700 text-sm">{goal.goal}</span>
                          </li>
                        ))
                      ) : (
                        <>
                          <li className="flex items-start space-x-2">
                            <span className="text-yellow-500 mt-1">⚡</span>
                            <span className="text-gray-700 text-sm">Streamline legacy systems to improve operational efficiency.</span>
                          </li>
                          <li className="flex items-start space-x-2">
                            <span className="text-yellow-500 mt-1">⚡</span>
                            <span className="text-gray-700 text-sm">Enhance cloud infrastructure for scalable content delivery.</span>
                          </li>
                          <li className="flex items-start space-x-2">
                            <span className="text-yellow-500 mt-1">⚡</span>
                            <span className="text-gray-700 text-sm">Enable data-driven decision-making through improved analytics.</span>
                          </li>
                        </>
                      )}
                    </ul>
                  </div>

                  {/* Priorities */}
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="text-md font-semibold text-gray-900">Priorities</h4>
                      {/* Hide See All button in presentation but keep functionality */}
                      <span className="text-blue-600 text-sm cursor-pointer presentation-hide">See All →</span>
                    </div>
                    <ul className="space-y-2">
                      {customerData?.goals?.priority_goals && customerData.goals.priority_goals.length > 0 ? (
                        customerData.goals.priority_goals.slice(0, 3).map((priority, index) => (
                          <li key={index} className="flex items-start space-x-2">
                            <span className="text-green-500 mt-1">🌱</span>
                            <span className="text-gray-700 text-sm">{priority.name}</span>
                          </li>
                        ))
                      ) : (
                        <>
                          <li className="flex items-start space-x-2">
                            <span className="text-green-500 mt-1">🌱</span>
                            <span className="text-gray-700 text-sm">Integrate data systems for real-time business insights.</span>
                          </li>
                          <li className="flex items-start space-x-2">
                            <span className="text-green-500 mt-1">🌱</span>
                            <span className="text-gray-700 text-sm">Improve internal IT workflows to support innovation at scale.</span>
                          </li>
                          <li className="flex items-start space-x-2">
                            <span className="text-green-500 mt-1">🌱</span>
                            <span className="text-gray-700 text-sm">Growing demand for cloud-native and edge computing solutions.</span>
                          </li>
                        </>
                      )}
                    </ul>
                  </div>

                  {/* Industry Trends */}
                  <div className="bg-white rounded-lg p-4 border border-gray-200">
                    <div className="flex justify-between items-center mb-3">
                      <h4 className="text-md font-semibold text-gray-900">Industry Trends</h4>
                      {/* Hide See All button in presentation but keep functionality */}
                      <span className="text-blue-600 text-sm cursor-pointer presentation-hide">See All →</span>
                    </div>
                    <ul className="space-y-2">
                      {customerData?.goals?.trend_goals && customerData.goals.trend_goals.length > 0 ? (
                        customerData.goals.trend_goals.slice(0, 3).map((trend, index) => (
                          <li key={index} className="flex items-start space-x-2">
                            <span className="text-purple-500 mt-1">📈</span>
                            <span className="text-gray-700 text-sm">{trend.TrendHighlights}</span>
                          </li>
                        ))
                      ) : (
                        <>
                          <li className="flex items-start space-x-2">
                            <span className="text-purple-500 mt-1">📈</span>
                            <span className="text-gray-700 text-sm">Increasing adoption of AI/ML in content personalization.</span>
                          </li>
                          <li className="flex items-start space-x-2">
                            <span className="text-purple-500 mt-1">📈</span>
                            <span className="text-gray-700 text-sm">Emphasis on cybersecurity and data privacy in media enterprises.</span>
                          </li>
                          <li className="flex items-start space-x-2">
                            <span className="text-purple-500 mt-1">📈</span>
                            <span className="text-gray-700 text-sm">Growing demand for cloud-native and edge computing solutions.</span>
                          </li>
                        </>
                      )}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'project-output',
      title: 'Project Output - Without Modernization',
      description: 'Top projects without modernization overlay',
      slideType: 'project-output',
      component: (
        <div className="p-6 presentation-slide">
          <div className="max-w-7xl mx-auto">
            <div className="bg-white rounded-xl shadow-lg p-6 flex flex-col" data-presentation-graph-card="true">
              <div className="w-full">
                {projectOutputFlags.hasNonModernizationProjects ? (
                  <OutputGraph
                    projects={customerData?.top_10_projects?.top_projects}
                    hideCheckbox={true}
                    hideControls={true}
                    showModernizationProjects={false}
                    customTitle="Project Output - Without Modernization"
                    hideDescription={true}
                    presentationMode={true}
                    controlsVariant="mapButtons"
                    mapMinWidth={DEMO_OUTPUT_MAP_MIN_WIDTH}
                    mapMinHeight={DEMO_OUTPUT_MAP_MIN_HEIGHT}
                    mapMaxHeight={DEMO_OUTPUT_MAP_MAX_HEIGHT}
                    mapHeightRatio={DEMO_OUTPUT_MAP_HEIGHT_RATIO}
                    customerName={customerData?.customer_details?.name}
                    headerAction={downloadButton}
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center min-h-[400px] text-gray-500">
                    <h1 className="text-xl sm:text-2xl font-bold italic text-[#0F2A4F] mb-4">Project Output - Without Modernization</h1>
                    <p>No projects found without modernization</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )
    },
    {
      id: 'project-output-modernization',
      title: 'Project Output - With Modernization',
      description: 'Top projects with modernization overlay',
      slideType: 'project-output-modernization',
      component: (
        <div className="p-6 presentation-slide">
          <div className="max-w-7xl mx-auto">
            <div className="bg-white rounded-xl shadow-lg px-4 py-2 flex flex-col" data-presentation-graph-card="true">
              <div className="w-full">
                {projectOutputFlags.hasModernizationProjects ? (
                  <OutputGraph
                    projects={customerData?.top_10_projects?.top_projects}
                    hideCheckbox={true}
                    hideControls={true}
                    showModernizationProjects={true}
                    customTitle="Project Output - With Modernization"
                    hideDescription={true}
                    presentationMode={true}
                    controlsVariant="mapButtons"
                    mapMinWidth={DEMO_OUTPUT_MAP_MIN_WIDTH}
                    mapMinHeight={DEMO_OUTPUT_MAP_MIN_HEIGHT}
                    mapMaxHeight={DEMO_OUTPUT_MAP_MAX_HEIGHT}
                    mapHeightRatio={DEMO_OUTPUT_MAP_HEIGHT_RATIO}
                    customerName={customerData?.customer_details?.name}
                    headerAction={downloadButton}
                  />
                ) : (
                  <div className="flex flex-col items-center justify-center min-h-[400px] text-gray-500">
                    <h1 className="text-xl sm:text-2xl font-bold italic text-[#0F2A4F] mb-4">Project Output - With Modernization</h1>
                    <p>No modernization projects found.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )
    },
    ...PRESENTATION_MATURITY_PLANS.map((plan) => ({
      id: `maturity-${plan.plan_id}`,
      title: `Maturity Transformation Graph - ${plan.name}`,
      description: plan.name,
      slideType: 'maturity-graph' as const,
      maturityPlanId: plan.plan_id,
      maturityPlanName: plan.name,
      component: (
        <div className="p-3 presentation-slide presentation-slide-maturity" key={plan.plan_id}>
          <div className="max-w-7xl mx-auto">
            <div className="bg-white rounded-xl shadow-lg px-4 py-2 w-full">
              <TransformationMaturityGraph
                customerId={customerData?.customer_id}
                selectedPlanId={plan.plan_id}
                hideDropdown={true}
                customTitle={`Maturity Transformation Graph - ${plan.name}`}
                hideDescription={true}
                compactPresentation={true}
                headerAction={downloadButton}
              />
            </div>
          </div>
        </div>
      )
    })),
    {
      id: 'transformation-roadmap',
      title: 'Transformation Milestones & Roadmap',
      description: 'Project timeline and dependencies',
      slideType: 'transformation-roadmap',
      component: (
        <div className="p-6 presentation-slide">
          <div className="max-w-7xl mx-auto">
            <div className="bg-white rounded-xl shadow-lg p-6 flex flex-col border border-gray-200">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold text-gray-900">Transformation Milestones & Roadmap</h2>
                {(roadmapData?.tasks?.length ?? 0) > 0 && (
                  <button
                    data-download-png
                    onClick={() => downloadPngRef.current?.()}
                    disabled={isDownloadingPng}
                    className="inline-flex items-center shrink-0 px-4 py-2 bg-gray-600 text-white text-sm font-medium rounded-lg hover:bg-gray-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed min-w-[140px]"
                  >
                    <ArrowDownTrayIcon className="w-4 h-4 mr-2 shrink-0" />
                    {isDownloadingPng ? 'Downloading...' : 'Download as PNG'}
                  </button>
                )}
              </div>
              <div className="w-full overflow-x-auto" style={{ minWidth: 0 }}>
                <TransformationRoadmapChart 
                  width={1200} 
                  height={500}
                  data={roadmapData}
                  showTooltips={true}
                  showLegends={false}
                  interactive={true}
                />
              </div>
            </div>
          </div>
        </div>
      )
    }
    ];
    return baseSlides;
  }, [customerData, roadmapData, isDownloadingPng, projectOutputFlags]);

  const nextSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev + 1) % slides.length);
  }, [slides.length]);

  const previousSlide = useCallback(() => {
    setCurrentSlide((prev) => (prev - 1 + slides.length) % slides.length);
  }, [slides.length]);

  // Handle keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (!isOpen) return;

      switch (event.key) {
        case 'ArrowRight':
        case 'ArrowDown':
        case ' ':
          event.preventDefault();
          nextSlide();
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          event.preventDefault();
          previousSlide();
          break;
        case 'Escape':
          event.preventDefault();
          onClose();
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, nextSlide, previousSlide, onClose]);

  // Auto-enter fullscreen when component opens
  useEffect(() => {
    if (isOpen) {
      const enterFullscreen = async () => {
        try {
          if (!document.fullscreenElement) {
            await document.documentElement.requestFullscreen();
          }
        } catch (error) {
          console.log('Fullscreen not supported or denied');
        }
      };
      
      // Small delay to ensure component is fully rendered
      setTimeout(enterFullscreen, 100);
    }
  }, [isOpen]);

  // Fix SVG alignment in presentation mode - use MutationObserver for immediate application
  useEffect(() => {
    if (!isOpen) return;

    const fixSVGElement = (svgElement: SVGSVGElement) => {
      // Get natural dimensions
      const widthAttr = svgElement.getAttribute('width');
      const heightAttr = svgElement.getAttribute('height');
      const viewBox = svgElement.getAttribute('viewBox');
      
      // If SVG has width/height but no viewBox, create one immediately
      if (widthAttr && heightAttr && !viewBox) {
        const width = parseFloat(widthAttr.replace('px', '')) || 1200;
        const height = parseFloat(heightAttr.replace('px', '')) || 600;
        svgElement.setAttribute('viewBox', `0 0 ${width} ${height}`);
      }
      
      // Set preserveAspectRatio to center and scale content properly
      // This ensures content is centered when SVG is stretched to 100% width
      if (!svgElement.getAttribute('preserveAspectRatio')) {
        svgElement.setAttribute('preserveAspectRatio', 'xMidYMid meet');
      }
    };

    // Fix existing SVGs immediately
    const existingSvgs = document.querySelectorAll('.presentation-slide svg');
    existingSvgs.forEach((svg) => fixSVGElement(svg as SVGSVGElement));

    // Watch for new SVG elements being added to the DOM
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType === Node.ELEMENT_NODE) {
            const element = node as Element;
            // Check if the added node is an SVG
            if (element.tagName === 'svg' && element.closest('.presentation-slide')) {
              fixSVGElement(element as SVGSVGElement);
            }
            // Check for SVGs within the added node
            const svgs = element.querySelectorAll?.('svg');
            if (svgs) {
              svgs.forEach((svg) => {
                if (svg.closest('.presentation-slide')) {
                  fixSVGElement(svg);
                }
              });
            }
          }
        });
      });
    });

    // Observe the presentation slide container for changes
    const slideContainer = document.querySelector('.presentation-slide');
    if (slideContainer) {
      observer.observe(slideContainer, {
        childList: true,
        subtree: true
      });
    }

    // Also observe the entire presentation area
    const presentationArea = presentationRef.current;
    if (presentationArea) {
      observer.observe(presentationArea, {
        childList: true,
        subtree: true
      });
    }

    return () => {
      observer.disconnect();
    };
  }, [isOpen, currentSlide]);

  // Helper function to convert SVG to image
  // Uses natural SVG dimensions (viewBox or explicit attributes) to avoid capturing empty space
  const svgToImage = async (svgElement: SVGSVGElement, scaleFactor: number = 2): Promise<string> => {
    return new Promise((resolve, reject) => {
      try {
        // Clone the SVG to avoid modifying the original
        const clonedSvg = svgElement.cloneNode(true) as SVGSVGElement;
        
        // Get SVG dimensions - prioritize natural dimensions over stretched presentation dimensions
        let svgWidth = 0;
        let svgHeight = 0;
        
        // Method 1: Try viewBox first (this gives us the natural content dimensions)
        // This is most important because it represents the actual content bounds
        const viewBox = svgElement.getAttribute('viewBox');
        if (viewBox) {
          const viewBoxValues = viewBox.split(/\s+|,/);
          if (viewBoxValues.length >= 4) {
            svgWidth = parseFloat(viewBoxValues[2]) || 0;
            svgHeight = parseFloat(viewBoxValues[3]) || 0;
          }
        }
        
        // Method 2: Try explicit width/height attributes (natural dimensions)
        if (svgWidth === 0 || svgHeight === 0) {
          const widthAttr = svgElement.getAttribute('width');
          const heightAttr = svgElement.getAttribute('height');
          if (widthAttr && heightAttr) {
            // Remove 'px' if present and parse
            svgWidth = parseFloat(widthAttr.replace('px', '')) || 0;
            svgHeight = parseFloat(heightAttr.replace('px', '')) || 0;
          }
        }
        
        // Method 3: If still no dimensions, try to calculate from content bounds
        // This helps when SVG is stretched but content has natural bounds
        if (svgWidth === 0 || svgHeight === 0) {
          // Try to get the actual content bounds by checking the SVG's children
          const bbox = svgElement.getBBox();
          if (bbox.width > 0 && bbox.height > 0) {
            svgWidth = bbox.width + bbox.x; // Add x offset
            svgHeight = bbox.height + bbox.y; // Add y offset
          }
        }
        
        // Method 4: Use getBoundingClientRect only if we still don't have dimensions
        // This is last resort as it may give stretched dimensions in presentation mode
        if (svgWidth === 0 || svgHeight === 0) {
          const rect = svgElement.getBoundingClientRect();
          if (rect.width > 0 && rect.height > 0) {
            svgWidth = rect.width;
            svgHeight = rect.height;
          }
        }
        
        // Final fallback: use default dimensions
        if (svgWidth === 0 || svgHeight === 0) {
          svgWidth = 1200;
          svgHeight = 600;
          console.warn(`SVG dimensions not found, using defaults: ${svgWidth}x${svgHeight}`);
        }
        
        // Remove any CSS-based width/height from cloned SVG to use natural dimensions
        clonedSvg.removeAttribute('style');
        
        // Set explicit dimensions on cloned SVG using natural dimensions
        clonedSvg.setAttribute('width', svgWidth.toString());
        clonedSvg.setAttribute('height', svgHeight.toString());
        
        // Ensure viewBox is set to match dimensions (prevents scaling issues)
        if (!clonedSvg.getAttribute('viewBox') && svgWidth > 0 && svgHeight > 0) {
          clonedSvg.setAttribute('viewBox', `0 0 ${svgWidth} ${svgHeight}`);
        } else if (viewBox) {
          // Keep original viewBox if it exists
          clonedSvg.setAttribute('viewBox', viewBox);
        }
        
        // Set preserveAspectRatio to center and scale content properly
        clonedSvg.setAttribute('preserveAspectRatio', 'xMidYMid meet');
        
        // Remove any CSS classes that might affect rendering
        clonedSvg.removeAttribute('class');
        
        // Serialize SVG to string
        const svgData = new XMLSerializer().serializeToString(clonedSvg);
        const svgBlob = new Blob([svgData], { type: 'image/svg+xml;charset=utf-8' });
        const url = URL.createObjectURL(svgBlob);
        
        // Create image element
        const img = new Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          // Use provided scale factor for higher resolution (default 2x, can be higher for roadmaps)
          const highResWidth = svgWidth * scaleFactor;
          const highResHeight = svgHeight * scaleFactor;
          
          canvas.width = highResWidth;
          canvas.height = highResHeight;
          
          const ctx = canvas.getContext('2d');
          if (ctx) {
            // Enable high-quality image smoothing
            ctx.imageSmoothingEnabled = true;
            ctx.imageSmoothingQuality = 'high';
            
            // Scale the context to draw at higher resolution
            ctx.scale(scaleFactor, scaleFactor);
            
            // Draw the image at natural size (will be rendered at higher resolution internally)
            ctx.drawImage(img, 0, 0, svgWidth, svgHeight);
            
            // Convert to data URL with high quality
            const dataUrl = canvas.toDataURL('image/png', 1.0); // Maximum quality
            URL.revokeObjectURL(url);
            resolve(dataUrl);
          } else {
            URL.revokeObjectURL(url);
            reject(new Error('Could not get canvas context'));
          }
        };
        img.onerror = (error) => {
          URL.revokeObjectURL(url);
          reject(new Error(`Failed to load SVG as image: ${error}`));
        };
        img.src = url;
      } catch (error) {
        reject(error);
      }
    });
  };

  const downloadCurrentGraphAsPNG = async () => {
    const graphSlideTypes = ['project-output', 'project-output-modernization', 'maturity-graph', 'transformation-roadmap'];
    const slide = slides[currentSlide];
    if (!slide) return;
    if (!graphSlideTypes.includes(slide.slideType)) return;

    setIsDownloadingPng(true);
    try {
      const customerName = customerData?.customer_details?.name || 'Customer';
      const sanitize = (s: string) => s.replace(/[^a-zA-Z0-9_-]/g, '_');
      const dateStr = new Date().toISOString().split('T')[0];
      const baseFilename = `${sanitize(customerName)}_${sanitize(slide.title)}_${dateStr}.png`;

      if (slide.slideType === 'transformation-roadmap') {
        const svgElement = document.querySelector('.presentation-slide .transformation-roadmap-chart svg') as SVGSVGElement;
        if (!svgElement) {
          alert('Could not find roadmap chart to download.');
          return;
        }
        const dataUrl = await svgToImage(svgElement, 4);
        const blob = await fetch(dataUrl).then((r) => r.blob());
        saveAs(blob, baseFilename);
      } else if (slide.slideType === 'project-output' || slide.slideType === 'project-output-modernization') {
        const svgElement = document.querySelector('.presentation-slide [data-output-graph-svg="true"]') as SVGSVGElement;
        if (!svgElement) {
          alert('Could not find project output graph to download.');
          return;
        }

        const dataUrl = await svgToImage(svgElement, 4);
        const blob = await fetch(dataUrl).then((r) => r.blob());
        saveAs(blob, baseFilename);
      } else {
        const cardElement = (
          document.querySelector('.presentation-slide [data-presentation-graph-card="true"]') ||
          document.querySelector('.presentation-slide .max-w-7xl > div.bg-white.rounded-xl')
        ) as HTMLElement;
        if (!cardElement) {
          alert('Could not find graph to download.');
          return;
        }
        const downloadButtons = cardElement.querySelectorAll('[data-download-png]');
        const originalDisplays: string[] = [];
        downloadButtons.forEach((el) => {
          const htmlEl = el as HTMLElement;
          originalDisplays.push(htmlEl.style.display);
          htmlEl.style.display = 'none';
        });
        try {
          const canvas = await html2canvas(cardElement, {
            scale: 4,
            useCORS: true,
            logging: false
          });
          const dataUrl = canvas.toDataURL('image/png', 1.0);
          const blob = await fetch(dataUrl).then((r) => r.blob());
          saveAs(blob, baseFilename);
        } finally {
          downloadButtons.forEach((el, i) => {
            (el as HTMLElement).style.display = originalDisplays[i] || '';
          });
        }
      }
    } catch (error: unknown) {
      console.error('Error downloading graph as PNG:', error);
      alert('Failed to download image. Please try again.');
    } finally {
      setIsDownloadingPng(false);
    }
  };
  downloadPngRef.current = downloadCurrentGraphAsPNG;

  // Helper function to add new page with customer name header
  const addNewPageWithHeader = (pdf: jsPDF, customerName: string, pageWidth: number): number => {
    pdf.addPage();
    pdf.setFontSize(18);
    pdf.setTextColor(30, 30, 30);
    pdf.text(customerName, pageWidth / 2, 15, { align: 'center' });
    return 25; // Return starting Y position
  };

  const exportAsPDF = async () => {
    try {
      // Show loading state
      const exportButton = document.querySelector('[data-export-pdf]') as HTMLButtonElement;
      if (exportButton) {
        exportButton.disabled = true;
        exportButton.innerHTML = 'Generating PDF...';
      }

      const pdf = new jsPDF('landscape', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      
      // Export each slide
      for (let i = 0; i < slides.length; i++) {
        const slide = slides[i];
        // Go to slide
        setCurrentSlide(i);
        
        // Wait for slide to render - use requestAnimationFrame to ensure DOM is updated
        await new Promise(resolve => {
          requestAnimationFrame(() => {
            setTimeout(resolve, 1000);
          });
        });
        
        // Hide header and footer temporarily
        const header = document.querySelector('.sticky.top-0') as HTMLElement;
        const footer = document.querySelector('.w-full.py-4.text-center.bg-gray-100') as HTMLElement;
        if (header) header.style.display = 'none';
        if (footer) footer.style.display = 'none';
        
        // Get slide content area - use presentation-slide class which is on each slide
        const slideContentArea = document.querySelector('.presentation-slide') as HTMLElement;
        if (!slideContentArea) {
          console.error(`Slide ${i} (${slides[i].title}) content not found. Selector: .presentation-slide`);
          // Still add a page to maintain structure, but skip content
          if (i > 0) {
            pdf.addPage();
          }
          const customerName = customerData?.customer_details?.name || 'Customer';
          pdf.setFontSize(18);
          pdf.setTextColor(30, 30, 30);
          pdf.text(customerName, pageWidth / 2, 15, { align: 'center' });
          pdf.setFontSize(12);
          pdf.text(`Slide ${i + 1}: ${slides[i].title} - Content not available`, pageWidth / 2, 30, { align: 'center' });
          continue;
        }
          
          // Add new page if not first slide
          if (i > 0) {
            pdf.addPage();
          }
          
          // Add customer name as header at top
          const customerName = customerData?.customer_details?.name || 'Customer';
          pdf.setFontSize(18);
          pdf.setTextColor(30, 30, 30);
        pdf.text(customerName, pageWidth / 2, 15, { align: 'center' });
        
        let currentY = 25; // Start below customer name
        const leftMargin = 20;
        const fullColumnWidth = pageWidth - 40; // Full width for single column layout
        
        // Process slide based on type
        if (slide.slideType === 'customer-overview') {
          // Customer Overview - Extract text content in single column
          const leftColumn = slideContentArea.querySelector('.grid .bg-white.rounded-lg.p-6') as HTMLElement;
          const rightColumn = slideContentArea.querySelector('.space-y-4') as HTMLElement;
          
          let yPosition = currentY;
          
          // Client Details
          if (leftColumn) {
            // Client Details Title
            pdf.setFontSize(14);
            pdf.setTextColor(0, 0, 0);
            pdf.setFont('helvetica', 'bold');
            pdf.text('Client Details', leftMargin, yPosition);
            yPosition += 8;
            
            // Extract each detail field
            const detailFields = leftColumn.querySelectorAll('.space-y-4 > div');
            pdf.setFont('helvetica', 'normal');
            pdf.setFontSize(10);
            
            detailFields.forEach((detail) => {
              const spans = detail.querySelectorAll('span');
              
              if (spans.length > 0) {
                // Handle multi-line text for paragraphs
                const isParagraph = detail.querySelector('p');
                if (isParagraph) {
                  let label = spans[0]?.textContent?.trim() || '';
                  const value = isParagraph.textContent?.trim() || '';
                  
                  // Remove colon if already present at the end
                  label = label.replace(/:\s*$/, '');
                  
                  pdf.setFont('helvetica', 'bold');
                  pdf.text(label + ':', leftMargin, yPosition);
                  yPosition += 5;
                  
                  pdf.setFont('helvetica', 'normal');
                  const lines = pdf.splitTextToSize(value, fullColumnWidth - 10);
                  pdf.text(lines, leftMargin + 5, yPosition);
                  yPosition += lines.length * 4.5 + 3;
                } else {
                  // For simple fields, check if label already has colon
                  let label = spans[0]?.textContent?.trim() || '';
                  const value = spans[1]?.textContent?.trim() || '';
                  
                  // Remove colon if already present at the end
                  label = label.replace(/:\s*$/, '');
                  
                  pdf.setFont('helvetica', 'bold');
                  pdf.text(label + ':', leftMargin, yPosition);
                  yPosition += 5;
                  
                  pdf.setFont('helvetica', 'normal');
                  const lines = pdf.splitTextToSize(value, fullColumnWidth - 10);
                  pdf.text(lines, leftMargin + 5, yPosition);
                  yPosition += lines.length * 4.5 + 2;
                }
              }
            });
          }
          
          // Add spacing before Key Goals section
          yPosition += 8;
          
          // Prepare data for Key Goals, Priorities, Industry Trends
          const customerName = customerData?.customer_details?.name || 'Customer';
          const bottomMargin = 20;
          const titleFontSize = 12;
          const contentFontSize = 9;
          const titleLineHeight = 7;
          const contentLineHeight = 4;
          const spacingBetweenItems = 2;
          const spacingBetweenSections = 6;
          const textIndent = 3;
          
          // Collect data from DOM or customerData
          // Use a Map to ensure correct order and avoid duplicates
          const sectionsMap = new Map<string, { title: string; items: string[] }>();
          let cardsFound = false;
          
          if (rightColumn) {
            const cards = rightColumn.querySelectorAll('.bg-white.rounded-lg');
            if (cards.length > 0) {
              cardsFound = true;
              cards.forEach((card) => {
                const title = card.querySelector('h4');
                const titleText = title?.textContent?.trim() || '';
                const items: string[] = [];
                
                const listItems = card.querySelectorAll('li');
                listItems.forEach((item) => {
                  const spans = item.querySelectorAll('span');
                  let textContent = '';
                  if (spans.length > 1) {
                    textContent = spans[1]?.textContent?.trim() || '';
                  } else {
                    textContent = item.textContent?.trim() || '';
                    textContent = textContent.replace(/[⚡🌱📈]/g, '').trim();
                  }
                  if (textContent) items.push(textContent);
                });
                
                if (titleText && items.length > 0) {
                  sectionsMap.set(titleText, { title: titleText, items });
                }
              });
            }
          }
          
          // Fallback: Extract from customerData
          if (!cardsFound && customerData) {
            if (customerData.goals?.key_goals && customerData.goals.key_goals.length > 0) {
              const items = customerData.goals.key_goals.slice(0, 3).map((g: any) => g.goal || '').filter(Boolean);
              if (items.length > 0) {
                sectionsMap.set('Key Goals', { title: 'Key Goals', items });
              }
            }
            if (customerData.goals?.priority_goals && customerData.goals.priority_goals.length > 0) {
              const items = customerData.goals.priority_goals.slice(0, 3).map((p: any) => p.name || '').filter(Boolean);
              if (items.length > 0) {
                sectionsMap.set('Priorities', { title: 'Priorities', items });
              }
            }
            if (customerData.goals?.trend_goals && customerData.goals.trend_goals.length > 0) {
              const items = customerData.goals.trend_goals.slice(0, 3).map((t: any) => t.TrendHighlights || '').filter(Boolean);
              if (items.length > 0) {
                sectionsMap.set('Industry Trends', { title: 'Industry Trends', items });
              }
            }
          }
          
          // Convert to array in correct order: Key Goals, Priorities, Industry Trends
          const sectionsData: Array<{ title: string; items: string[] }> = [];
          const sectionOrder = ['Key Goals', 'Priorities', 'Industry Trends'];
          sectionOrder.forEach(title => {
            if (sectionsMap.has(title)) {
              sectionsData.push(sectionsMap.get(title)!);
            }
          });
          
          // Calculate heights for each section
          const calculateSectionHeight = (section: { title: string; items: string[] }, pdfInstance: jsPDF): number => {
            let height = titleLineHeight; // Title height
            
            pdfInstance.setFontSize(contentFontSize);
            section.items.forEach((item) => {
              const lines = pdfInstance.splitTextToSize(item, fullColumnWidth - 10 - textIndent);
              height += lines.length * contentLineHeight + spacingBetweenItems;
            });
            
            return height;
          };
          
          // Check if content fits on current page
          const checkFitsOnPage = (startY: number, height: number): boolean => {
            return (startY + height) <= (pageHeight - bottomMargin);
          };
          
          // Process sections with overflow handling
          // Key requirement: All three sections (Key Goals, Priorities, Industry Trends) should stay together
          // on the same page unless they overflow together
          if (sectionsData.length > 0) {
            // Calculate total height of all sections together
            let totalSectionsHeight = 0;
            sectionsData.forEach((section) => {
              totalSectionsHeight += calculateSectionHeight(section, pdf);
              // Add spacing between sections (except after last)
              if (section !== sectionsData[sectionsData.length - 1]) {
                totalSectionsHeight += spacingBetweenSections;
              }
            });
            
            // Check if all sections fit together on current page
            // If not, move all sections to a new page
            if (!checkFitsOnPage(yPosition, totalSectionsHeight)) {
              yPosition = addNewPageWithHeader(pdf, customerName, pageWidth);
            }
            
            // Now render all sections together on the current page
            sectionsData.forEach((section, index) => {
              // Add section title
              pdf.setFontSize(titleFontSize);
              pdf.setFont('helvetica', 'bold');
              pdf.text(section.title, leftMargin, yPosition);
              yPosition += titleLineHeight;
              
              // Add section items with overflow handling
              pdf.setFont('helvetica', 'normal');
              pdf.setFontSize(contentFontSize);
              
              section.items.forEach((item) => {
                const lines = pdf.splitTextToSize(item, fullColumnWidth - 10 - textIndent);
                let remainingLines = lines;
                
                while (remainingLines.length > 0) {
                  // Calculate how many lines fit on current page
                  const availableHeight = pageHeight - yPosition - bottomMargin;
                  const linesThatFit = Math.floor(availableHeight / contentLineHeight);
                  
                  if (linesThatFit <= 0 || yPosition + contentLineHeight > pageHeight - bottomMargin) {
                    // Need new page for this item's overflow
                    yPosition = addNewPageWithHeader(pdf, customerName, pageWidth);
                    continue;
                  }
                  
                  // Take lines that fit on current page
                  const linesForThisPage = remainingLines.slice(0, Math.min(linesThatFit, remainingLines.length));
                  
                  pdf.text(linesForThisPage, leftMargin + textIndent, yPosition);
                  yPosition += linesForThisPage.length * contentLineHeight;
                  
                  // Remove processed lines
                  remainingLines = remainingLines.slice(linesForThisPage.length);
                  
                  // Add spacing after item if it's complete
                  if (remainingLines.length === 0) {
                    yPosition += spacingBetweenItems;
                  }
                }
              });
              
              // Add spacing between sections (except after last section)
              if (index < sectionsData.length - 1) {
                yPosition += spacingBetweenSections;
              }
            });
          }
        } else if (slide.slideType === 'project-output' || slide.slideType === 'project-output-modernization') {
          // Project Output - Extract heading, description, and SVG as image
          let yPosition = currentY;
          
          // Extract heading "Project Output"
          const headingElement = slideContentArea.querySelector('h1');
          let headingText = 'Project Output';
          if (headingElement) {
            headingText = headingElement.textContent?.trim() || 'Project Output';
          }
          
          pdf.setFontSize(16);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(15, 42, 79); // #0F2A4F color
          pdf.text(headingText, leftMargin, yPosition);
          yPosition += 10;
          
          // Extract description paragraph
          const descriptionElement = slideContentArea.querySelector('.text-zinc-500 p');
          let descriptionText = `This Sankey diagram shows ${customerName}'s internal systems. It maps how legacy systems support departments, modernized through cloud platforms and AI, impacting efficiency and innovation.`;
          
          if (descriptionElement) {
            descriptionText = descriptionElement.textContent?.trim() || descriptionText;
          }
          
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor(113, 113, 122); // zinc-500 color
          const descriptionLines = pdf.splitTextToSize(descriptionText, pageWidth - 40);
          pdf.text(descriptionLines, leftMargin, yPosition);
          yPosition += descriptionLines.length * 5 + 8; // Add spacing after description
          
          // Extract ONLY the SVG as image (excludes heading, description, and controls)
          const svgElement = slideContentArea.querySelector('[data-output-graph-svg="true"]') as SVGSVGElement;
          
          if (svgElement) {
            try {
              // Use svgToImage to capture ONLY the SVG element itself
              // This excludes any container elements, headings, descriptions, etc.
              const svgImage = await svgToImage(svgElement);
              
              // Use fixed target width for PDF regardless of screen resolution
              // This ensures consistent sizing across all screen resolutions
              const targetWidth = pageWidth - 40; // mm - fixed width for PDF
              const maxHeight = pageHeight - yPosition - 20; // mm
              
              // Get SVG dimensions from the element's attributes or viewBox
              let svgWidth = 0;
              let svgHeight = 0;
              
              // Try to get dimensions from width/height attributes
              const widthAttr = svgElement.getAttribute('width');
              const heightAttr = svgElement.getAttribute('height');
              if (widthAttr && heightAttr) {
                svgWidth = parseFloat(widthAttr) || 0;
                svgHeight = parseFloat(heightAttr) || 0;
              }
              
              // Try viewBox if width/height not found
              if (svgWidth === 0 || svgHeight === 0) {
                const viewBox = svgElement.getAttribute('viewBox');
                if (viewBox) {
                  const viewBoxValues = viewBox.split(/\s+|,/);
                  if (viewBoxValues.length >= 4) {
                    svgWidth = parseFloat(viewBoxValues[2]) || 0;
                    svgHeight = parseFloat(viewBoxValues[3]) || 0;
                  }
                }
              }
              
              // Use standard dimensions for Sankey diagram if not found
              if (svgWidth === 0 || svgHeight === 0) {
                svgWidth = 1200;
                svgHeight = 600;
              }
              
              // Calculate dimensions to fit page while maintaining aspect ratio
              const aspectRatio = svgWidth / svgHeight;
              let imgWidth = targetWidth; // Always use fixed width
              let imgHeight = targetWidth / aspectRatio;
              
              // If height exceeds available space, scale down proportionally
              if (imgHeight > maxHeight) {
                imgHeight = maxHeight;
                imgWidth = maxHeight * aspectRatio;
              }
              
              const imgX = (pageWidth - imgWidth) / 2; // Center horizontally
              pdf.addImage(svgImage, 'PNG', imgX, yPosition, imgWidth, imgHeight);
            } catch (error) {
              console.error('Error converting SVG to image:', error);
              // Fallback: use html2canvas on just the SVG element's parent container (flex justify-center)
              const svgContainer = svgElement.closest('[data-output-graph-container="true"]') as HTMLElement;
              if (svgContainer) {
                try {
                  const canvas = await html2canvas(svgContainer as HTMLElement, {
                    scale: 2,
                    backgroundColor: '#ffffff',
                    useCORS: true,
                    logging: false
                  });
                  
                  const imgData = canvas.toDataURL('image/png');
                  const targetWidth = pageWidth - 40;
                  const maxHeight = pageHeight - yPosition - 20;
                  const aspectRatio = canvas.width / canvas.height;
                  let imgWidth = targetWidth;
                  let imgHeight = targetWidth / aspectRatio;
                  
                  if (imgHeight > maxHeight) {
                    imgHeight = maxHeight;
                    imgWidth = maxHeight * aspectRatio;
                  }
                  
                  const imgX = (pageWidth - imgWidth) / 2;
                  pdf.addImage(imgData, 'PNG', imgX, yPosition, imgWidth, imgHeight);
                } catch (canvasError) {
                  console.error('Error with html2canvas fallback:', canvasError);
                }
              }
            }
          }
        } else if (slide.slideType === 'maturity-graph') {
          // Maturity Graph - Each slide already has the correct graph rendered (no dropdown iteration)
          const headingElement = slideContentArea.querySelector('h2');
          const descriptionElement = slideContentArea.querySelector('p');
          const headingText = headingElement?.textContent?.trim() || 'Maturity Journey';
          const descriptionText = descriptionElement?.textContent?.trim() ||
            'This maturity journey visualization shows the 28-step transformation path from network foundation to advanced AI-enabled operations. The visualization tracks progress across the transformation journey and highlights the path to achieving AI-powered network automation.';
          const planName = slide.maturityPlanName || '';
            
          let yPosition = currentY;
          
          // Add heading
          pdf.setFontSize(16);
          pdf.setFont('helvetica', 'bold');
          pdf.setTextColor(15, 42, 79);
          pdf.text(headingText, leftMargin, yPosition);
          yPosition += 8;
          
          // Add description
          pdf.setFontSize(10);
          pdf.setFont('helvetica', 'normal');
          pdf.setTextColor(113, 113, 122);
          const descriptionLines = pdf.splitTextToSize(descriptionText, pageWidth - 40);
          pdf.text(descriptionLines, leftMargin, yPosition);
          yPosition += descriptionLines.length * 5 + 8;
          
          // Add plan name as subtitle
          if (planName) {
            pdf.setFontSize(12);
            pdf.setFont('helvetica', 'bold');
            pdf.setTextColor(0, 0, 0);
            pdf.text(planName, leftMargin, yPosition);
            yPosition += 8;
          }
          
          // Capture the Maturity Graph container (legend + SVG or empty state)
          const chartContainer = slideContentArea.querySelector('#maturity-journey-graph') as HTMLElement;
          if (chartContainer) {
            try {
              const canvas = await html2canvas(chartContainer, {
                scale: 2,
                backgroundColor: '#ffffff'
              });
              const imgData = canvas.toDataURL('image/png');
              const imgWidth = pageWidth - 40;
              const imgHeight = (canvas.height * imgWidth) / canvas.width;
              pdf.addImage(imgData, 'PNG', 20, yPosition, imgWidth, imgHeight);
            } catch (error) {
              console.error('Error capturing maturity graph:', error);
              const svgElement = chartContainer.querySelector('svg') as SVGSVGElement;
              if (svgElement) {
                try {
                  const svgImage = await svgToImage(svgElement);
                  const maxWidth = pageWidth - 40;
                  const maxHeight = pageHeight - yPosition - 20;
                  const svgWidth = svgElement.getBoundingClientRect().width || 1000;
                  const svgHeight = svgElement.getBoundingClientRect().height || 500;
                  const scale = Math.min(maxWidth / svgWidth, maxHeight / svgHeight);
                  const imgWidth = svgWidth * scale;
                  const imgHeight = svgHeight * scale;
                  const imgX = (pageWidth - imgWidth) / 2;
                  pdf.addImage(svgImage, 'PNG', imgX, yPosition, imgWidth, imgHeight);
                } catch (svgError) {
                  pdf.setFontSize(10);
                  pdf.setTextColor(113, 113, 122);
                  pdf.text('No maturity data available for this plan.', leftMargin, yPosition);
                }
              } else {
                pdf.setFontSize(10);
                pdf.setTextColor(113, 113, 122);
                pdf.text('No maturity data available for this plan.', leftMargin, yPosition);
              }
            }
          }
        } else if (slide.slideType === 'transformation-roadmap') {
          // Transformation Roadmap - Extract title and SVG as image
          const roadmapTitle = slideContentArea.querySelector('h2');
          if (roadmapTitle) {
            pdf.setFontSize(16);
            pdf.setTextColor(0, 0, 0);
            pdf.setFont('helvetica', 'bold');
            const titleText = roadmapTitle.textContent?.trim() || 'Transformation Milestones & Roadmap';
            pdf.text(titleText, 20, currentY);
            currentY += 10;
          }
          
          // Capture the full roadmap SVG directly (not just visible portion)
          // This ensures we get the complete scrollable content
          const svgElement = slideContentArea.querySelector('.transformation-roadmap-chart svg') as SVGSVGElement;
          
          if (svgElement) {
            try {
              // Use svgToImage to capture the full SVG at high resolution
              // Use scale factor of 2 for good quality in PDF
              const svgImage = await svgToImage(svgElement, 2);
              
              // Get the actual SVG dimensions for proper aspect ratio
              let svgWidth = 1200;
              let svgHeight = 500;
              
              // Try to get dimensions from SVG
              const viewBox = svgElement.getAttribute('viewBox');
              if (viewBox) {
                const viewBoxValues = viewBox.split(/\s+|,/);
                if (viewBoxValues.length >= 4) {
                  svgWidth = parseFloat(viewBoxValues[2]) || 1200;
                  svgHeight = parseFloat(viewBoxValues[3]) || 500;
                }
              } else {
                const widthAttr = svgElement.getAttribute('width');
                const heightAttr = svgElement.getAttribute('height');
                if (widthAttr && heightAttr) {
                  svgWidth = parseFloat(widthAttr.replace('px', '')) || 1200;
                  svgHeight = parseFloat(heightAttr.replace('px', '')) || 500;
                }
              }
              
              // Use fixed target width for PDF regardless of screen resolution
              const targetWidth = pageWidth - 40; // mm - fixed width for PDF
              const maxHeight = pageHeight - currentY - 20; // mm
              
              // Calculate dimensions to fit page while maintaining aspect ratio
              const aspectRatio = svgWidth / svgHeight;
              let imgWidth = targetWidth; // Always use fixed width
              let imgHeight = targetWidth / aspectRatio;
              
              // If height exceeds available space, scale down proportionally
              if (imgHeight > maxHeight) {
                imgHeight = maxHeight;
                imgWidth = maxHeight * aspectRatio;
              }
              
              const imgX = (pageWidth - imgWidth) / 2; // Center horizontally
              pdf.addImage(svgImage, 'PNG', imgX, currentY, imgWidth, imgHeight);
            } catch (error) {
              console.error('Error capturing Transformation Roadmap:', error);
              // Fallback: try html2canvas on the container (may only capture visible portion)
              const roadmapContainer = svgElement.closest('.transformation-roadmap-chart') as HTMLElement;
              if (roadmapContainer) {
                try {
                  const canvas = await html2canvas(roadmapContainer as HTMLElement, {
                    scale: 2,
                    backgroundColor: '#ffffff',
                    useCORS: true,
                    logging: false,
                    width: roadmapContainer.scrollWidth,
                    height: roadmapContainer.scrollHeight
                  });
                  
                  const imgData = canvas.toDataURL('image/png');
                  const targetWidth = pageWidth - 40;
                  const maxHeight = pageHeight - currentY - 20;
                  const aspectRatio = canvas.width / canvas.height;
                  let imgWidth = targetWidth;
                  let imgHeight = targetWidth / aspectRatio;
                  
                  if (imgHeight > maxHeight) {
                    imgHeight = maxHeight;
                    imgWidth = maxHeight * aspectRatio;
                  }
                  
                  const imgX = (pageWidth - imgWidth) / 2;
                  pdf.addImage(imgData, 'PNG', imgX, currentY, imgWidth, imgHeight);
                } catch (html2canvasError) {
                  console.error('Error with html2canvas fallback:', html2canvasError);
                }
              }
            }
          }
        }
        
        // Show header and footer again
        if (header) header.style.display = '';
        if (footer) footer.style.display = '';
      }
      
      // Save PDF
      const fileName = `${customerData?.customer_details?.name || 'Customer'}_Presentation_${new Date().toISOString().split('T')[0]}.pdf`;
      pdf.save(fileName);
      
      // Reset button state
      if (exportButton) {
        exportButton.disabled = false;
        exportButton.innerHTML = 'Export as PDF';
      }
      
      // Return to first slide
      setCurrentSlide(0);
      
    } catch (error) {
      console.error('Error generating PDF:', error);
      
      // Reset button state on error
      const exportButton = document.querySelector('[data-export-pdf]') as HTMLButtonElement;
      if (exportButton) {
        exportButton.disabled = false;
        exportButton.innerHTML = 'Export as PDF';
      }
      
      // Ensure header and footer are visible again on error
      const header = document.querySelector('.sticky.top-0') as HTMLElement;
      const footer = document.querySelector('.w-full.py-4.text-center.bg-gray-100') as HTMLElement;
      if (header) header.style.display = '';
      if (footer) footer.style.display = '';
      
      alert('Error generating PDF. Please try again.');
    }
  };

  // Helper function to load PPT template file
  const loadFile = (url: string, callback: (error: Error | null, content?: any) => void) => {
    PizZipUtils.getBinaryContent(url, callback);
  };

  // Helper function to convert image to buffer
  const imageToBuffer = (imageData: string): Uint8Array => {
    const base64Data = imageData.split(',')[1];
    const byteCharacters = atob(base64Data);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    return new Uint8Array(byteNumbers);
  };

  // Helper function to resize image to exact target dimensions with high quality
  // When preserveSharpness is true (for charts with text), disables smoothing to keep edges crisp
  const resizeImageToDimensions = (
    imageData: string,
    targetWidth: number,
    targetHeight: number,
    preserveSharpness = false
  ): Promise<string> => {
    return new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = targetWidth;
        canvas.height = targetHeight;
        
        const ctx = canvas.getContext('2d');
        if (ctx) {
          ctx.imageSmoothingEnabled = !preserveSharpness;
          if (!preserveSharpness) {
            ctx.imageSmoothingQuality = 'high';
          }
          
          ctx.drawImage(img, 0, 0, targetWidth, targetHeight);
          
          // Convert to data URL with maximum quality
          const resizedDataUrl = canvas.toDataURL('image/png', 1.0); // Maximum quality
          resolve(resizedDataUrl);
        } else {
          reject(new Error('Could not get canvas context'));
        }
      };
      img.onerror = () => reject(new Error('Failed to load image'));
      img.src = imageData;
    });
  };

  // Helper function to clean and format text for PowerPoint
  // Removes excessive whitespace and preserves natural paragraph breaks
  // PowerPoint will handle automatic word wrapping within text boxes
  const formatTextForPPT = (text: string): string => {
    if (!text || text.trim().length === 0) {
      return text;
    }
    
    // Split by existing line breaks to preserve intentional paragraph breaks
    const paragraphs = text.split('\n');
    const cleanedParagraphs: string[] = [];
    
    paragraphs.forEach(paragraph => {
      // Clean up whitespace: trim and replace multiple spaces with single space
      const cleaned = paragraph.trim().replace(/\s+/g, ' ');
      
      // Only add non-empty paragraphs
      if (cleaned.length > 0) {
        cleanedParagraphs.push(cleaned);
      }
    });
    
    // Join paragraphs with single line breaks (PowerPoint will handle wrapping)
    // This preserves paragraph structure while letting PowerPoint wrap within each paragraph
    return cleanedParagraphs.join('\n');
  };

  const exportAsPPT = async () => {
    try {
      // Show loading state
      const exportButton = document.querySelector('[data-export-ppt]') as HTMLButtonElement;
      if (exportButton) {
        exportButton.disabled = true;
        exportButton.innerHTML = 'Generating PPT...';
      }

      const customerName = customerData?.customer_details?.name || 'Customer';
      
      // Store all slide data
      const slideData: Array<{
        title: string;
        content?: string;
        image?: string;
        imageWidth?: number;
        imageHeight?: number;
        maturityPlanName?: string;
        slideType?: string;
        maturitySlideIndex?: number;
        projectOutputIndex?: number;
      }> = [];

      // Track type-specific indices for template placeholders
      let projectOutputIndex = 0;
      let maturitySlideIndex = 0;

      // Process each slide similar to PDF export
      for (let i = 0; i < slides.length; i++) {
        const slide = slides[i];
        // Go to slide
        setCurrentSlide(i);
        
        // Wait for slide to render
        await new Promise(resolve => {
          requestAnimationFrame(() => {
            setTimeout(resolve, 1000);
          });
        });
        
        // Hide header and footer temporarily
        const header = document.querySelector('.sticky.top-0') as HTMLElement;
        const footer = document.querySelector('.w-full.py-4.text-center.bg-gray-100') as HTMLElement;
        if (header) header.style.display = 'none';
        if (footer) footer.style.display = 'none';
        
        // Get slide content area
        const slideContentArea = document.querySelector('.presentation-slide') as HTMLElement;
        if (!slideContentArea) {
          continue;
        }

        if (slide.slideType === 'customer-overview') {
          // Customer Overview - Extract text content
          const leftColumn = slideContentArea.querySelector('.grid .bg-white.rounded-lg.p-6') as HTMLElement;
          const rightColumn = slideContentArea.querySelector('.space-y-4') as HTMLElement;
          
          let contentText = '';
          
          // Extract Client Details
          if (leftColumn) {
            const detailFields = leftColumn.querySelectorAll('.space-y-4 > div');
            detailFields.forEach((detail) => {
              const spans = detail.querySelectorAll('span');
              if (spans.length > 0) {
                const isParagraph = detail.querySelector('p');
                if (isParagraph) {
                  let label = spans[0]?.textContent?.trim() || '';
                  const value = isParagraph.textContent?.trim() || '';
                  label = label.replace(/:\s*$/, '');
                  contentText += `${label}: ${value}\n\n`;
                } else {
                  let label = spans[0]?.textContent?.trim() || '';
                  const value = spans[1]?.textContent?.trim() || '';
                  label = label.replace(/:\s*$/, '');
                  contentText += `${label}: ${value}\n\n`;
                }
              }
            });
          }
          
          // Extract Key Goals, Priorities, Industry Trends
          if (rightColumn) {
            const cards = rightColumn.querySelectorAll('.bg-white.rounded-lg');
            cards.forEach((card) => {
              const title = card.querySelector('h4');
              const titleText = title?.textContent?.trim() || '';
              if (titleText) {
                contentText += `${titleText}:\n`;
                const listItems = card.querySelectorAll('li');
                listItems.forEach((item) => {
                  const spans = item.querySelectorAll('span');
                  let textContent = '';
                  if (spans.length > 1) {
                    textContent = spans[1]?.textContent?.trim() || '';
                  } else {
                    textContent = item.textContent?.trim() || '';
                    textContent = textContent.replace(/[⚡🌱📈]/g, '').trim();
                  }
                  if (textContent) {
                    contentText += `• ${textContent}\n`;
                  }
                });
                contentText += '\n';
              }
            });
          }
          
          slideData.push({
            title: 'Customer Overview',
            content: contentText.trim(),
            slideType: 'overview'
          });
        } else if (slide.slideType === 'project-output' || slide.slideType === 'project-output-modernization') {
          // Project Output - Extract heading, description, and SVG as image
          const headingElement = slideContentArea.querySelector('h1');
          const descriptionElement = slideContentArea.querySelector('.text-zinc-500 p');
          const svgElement = slideContentArea.querySelector('[data-output-graph-svg="true"]') as SVGSVGElement;
          
          let titleText = 'Project Output';
          let descriptionText = `This Sankey diagram shows ${customerName}'s internal systems. It maps how legacy systems support departments, modernized through cloud platforms and AI, impacting efficiency and innovation.`;
          
          if (headingElement) {
            titleText = headingElement.textContent?.trim() || titleText;
          }
          if (descriptionElement) {
            descriptionText = descriptionElement.textContent?.trim() || descriptionText;
          }
          
          let imageData = '';
          let imageWidth = 1200;
          let imageHeight = 600;
          
          if (svgElement) {
            try {
              // Get natural SVG dimensions first (before capture)
              // Prioritize viewBox as it represents actual content bounds
              const viewBox = svgElement.getAttribute('viewBox');
              if (viewBox) {
                const viewBoxValues = viewBox.split(/\s+|,/);
                if (viewBoxValues.length >= 4) {
                  imageWidth = parseFloat(viewBoxValues[2]) || 1200;
                  imageHeight = parseFloat(viewBoxValues[3]) || 600;
                }
              }
              
              // If no viewBox, try width/height attributes (natural dimensions)
              if (!viewBox) {
                const widthAttr = svgElement.getAttribute('width');
                const heightAttr = svgElement.getAttribute('height');
                if (widthAttr && heightAttr) {
                  imageWidth = parseFloat(widthAttr.replace('px', '')) || 1200;
                  imageHeight = parseFloat(heightAttr.replace('px', '')) || 600;
                }
              }
              
              // Use svgToImage to capture ONLY the SVG element at natural dimensions
              const svgImage = await svgToImage(svgElement);
              imageData = svgImage;
              
              console.log('Project Output SVG captured:', { 
                imageWidth, 
                imageHeight,
                viewBox: svgElement.getAttribute('viewBox'),
                widthAttr: svgElement.getAttribute('width'),
                heightAttr: svgElement.getAttribute('height')
              });
            } catch (error) {
              console.error('Error converting SVG to image:', error);
              // No fallback - we want only the SVG, not containers with whitespace
            }
          }
          
          slideData.push({
            title: titleText,
            content: descriptionText,
            image: imageData,
            imageWidth,
            imageHeight,
            slideType: 'project_output',
            projectOutputIndex: projectOutputIndex++
          });
        } else if (slide.slideType === 'maturity-graph') {
          // Maturity Graph - Each slide already has the correct graph rendered
          const headingElement = slideContentArea.querySelector('h2');
          const descriptionElement = slideContentArea.querySelector('p');
          const headingText = headingElement?.textContent?.trim() || 'Maturity Journey';
          let descriptionText = descriptionElement?.textContent?.trim() || '';
          if (!descriptionText) {
            const altDescription = slideContentArea.querySelector('.text-gray-600, .text-sm, p.text-gray-500');
            descriptionText = altDescription?.textContent?.trim() ||
              'This maturity journey visualization shows the 28-step transformation path from network foundation to advanced AI-enabled operations.';
          }
          const planName = slide.maturityPlanName || '';
          
          const chartContainer = slideContentArea.querySelector('#maturity-journey-graph') as HTMLElement;
          const svgElement = chartContainer?.querySelector('svg') as SVGSVGElement;
          
          let imageData = '';
          let imageWidth = 1000;
          let imageHeight = 500;
          
          if (svgElement) {
            try {
              const viewBox = svgElement.getAttribute('viewBox');
              if (viewBox) {
                const viewBoxValues = viewBox.split(/\s+|,/);
                if (viewBoxValues.length >= 4) {
                  imageWidth = parseFloat(viewBoxValues[2]) || 1000;
                  imageHeight = parseFloat(viewBoxValues[3]) || 500;
                }
              }
              if (!viewBox) {
                const widthAttr = svgElement.getAttribute('width');
                const heightAttr = svgElement.getAttribute('height');
                if (widthAttr && heightAttr) {
                  imageWidth = parseFloat(widthAttr.replace('px', '')) || 1000;
                  imageHeight = parseFloat(heightAttr.replace('px', '')) || 500;
                }
              }
              const svgImage = await svgToImage(svgElement, 4);
              imageData = svgImage;
            } catch (error) {
              console.error('Error capturing maturity graph SVG:', error);
            }
          }
          if (!imageData && chartContainer) {
            try {
              const canvas = await html2canvas(chartContainer, {
                scale: 4,
                backgroundColor: '#ffffff',
                useCORS: true,
                logging: false
              });
              imageData = canvas.toDataURL('image/png');
              imageWidth = canvas.width;
              imageHeight = canvas.height;
            } catch (fallbackError) {
              console.error('Error capturing maturity graph fallback:', fallbackError);
            }
          }
          
          slideData.push({
            title: headingText || 'Maturity Journey',
            content: descriptionText,
            image: imageData,
            imageWidth,
            imageHeight,
            maturityPlanName: planName,
            slideType: 'maturity',
            maturitySlideIndex: maturitySlideIndex++
          });
        } else if (slide.slideType === 'transformation-roadmap') {
          // Transformation Roadmap - Extract title and SVG as image
          const roadmapTitle = slideContentArea.querySelector('h2');
          const titleText = roadmapTitle?.textContent?.trim() || 'Transformation Milestones & Roadmap';
          
          // Capture ONLY the SVG element for Roadmap (no container whitespace)
          const svgElement = slideContentArea.querySelector('.transformation-roadmap-chart svg') as SVGSVGElement;
          
          let imageData = '';
          let imageWidth = 1200;
          let imageHeight = 500;
          
          if (svgElement) {
            try {
              // Get natural SVG dimensions first (before capture)
              const viewBox = svgElement.getAttribute('viewBox');
              if (viewBox) {
                const viewBoxValues = viewBox.split(/\s+|,/);
                if (viewBoxValues.length >= 4) {
                  imageWidth = parseFloat(viewBoxValues[2]) || 1200;
                  imageHeight = parseFloat(viewBoxValues[3]) || 500;
                }
              }
              
              // If no viewBox, try width/height attributes
              if (!viewBox) {
                const widthAttr = svgElement.getAttribute('width');
                const heightAttr = svgElement.getAttribute('height');
                if (widthAttr && heightAttr) {
                  imageWidth = parseFloat(widthAttr.replace('px', '')) || 1200;
                  imageHeight = parseFloat(heightAttr.replace('px', '')) || 500;
                }
              }
              
              // Use svgToImage to capture ONLY the SVG element at natural dimensions
              // Use higher scale factor (4x) for roadmap to ensure high resolution
              // Roadmaps can be very wide (3600px+), so we need high resolution
              const svgImage = await svgToImage(svgElement, 4);
              imageData = svgImage;
              
              console.log('Roadmap SVG captured:', { 
                imageWidth, 
                imageHeight,
                viewBox: svgElement.getAttribute('viewBox'),
                scaleFactor: 4
              });
            } catch (error) {
              console.error('Error capturing roadmap SVG:', error);
            }
          }
          
          slideData.push({
            title: titleText,
            image: imageData,
            imageWidth,
            imageHeight,
            slideType: 'roadmap'
          });
        }
        
        // Show header and footer again
        if (header) header.style.display = '';
        if (footer) footer.style.display = '';
      }
      
      // Load template and generate PPT
      loadFile('/template.pptx', async (error, content) => {
        if (error) {
          console.error('Error loading template:', error);
          alert('Error loading template. Please try again.');
          if (exportButton) {
            exportButton.disabled = false;
            exportButton.innerHTML = 'Export as PPT';
          }
          return;
        }

        try {
          const zip = new PizZip(content);
          
          // Store all images with their metadata - use descriptive keys that match template placeholders
          const imageMap = new Map<string, { buffer: Uint8Array; width: number; height: number }>();
          
          // Standard image width for PowerPoint - 1200px for browser-matching quality
          const STANDARD_IMAGE_WIDTH = 1200;
          // Dense charts with text need higher resolution for sharp output
          const MATURITY_IMAGE_WIDTH = 2400;
          // Higher resolution for roadmap images (they can be very wide)
          const ROADMAP_IMAGE_WIDTH = 2400;
          
          // Process images and create placeholders with meaningful names
          // Use Promise.all to process all images asynchronously
          const processImages = async () => {
            const imagePromises = slideData.map(async (slide, slideIndex) => {
              if (slide.image && slide.image.length > 0) {
                // Create meaningful image keys based on slide type (match template placeholders)
                let imageKey = '';
                if (slide.slideType === 'project_output') {
                  const idx = slide.projectOutputIndex ?? 0;
                  imageKey = idx === 0 ? 'project_output_image' : 'project_output_image_1';
                } else if (slide.slideType === 'maturity') {
                  const idx = slide.maturitySlideIndex ?? 0;
                  imageKey = idx === 0 ? 'maturity_journey_image' : 'maturity_journey_image_1';
                } else if (slide.slideType === 'roadmap') {
                  imageKey = 'roadmap_image';
                } else {
                  imageKey = `image_${slideIndex}`;
                }
                
                try {
                  // Calculate aspect ratio from original image dimensions
                  const originalWidth = slide.imageWidth || 1200;
                  const originalHeight = slide.imageHeight || 600;
                  const aspectRatio = originalWidth / originalHeight;
                  
                  // Use higher resolution for roadmap and maturity (dense charts), standard for others
                  const targetWidth = slide.slideType === 'roadmap' ? ROADMAP_IMAGE_WIDTH
                    : slide.slideType === 'maturity' ? MATURITY_IMAGE_WIDTH
                    : STANDARD_IMAGE_WIDTH;
                  
                  // Calculate height to maintain aspect ratio
                  const finalWidth = targetWidth;
                  const finalHeight = Math.round(targetWidth / aspectRatio);
                  
                  // Resize the image to match the exact dimensions we'll report to PowerPoint
                  // This is critical - the buffer dimensions must match what we tell PowerPoint
                  // Use preserveSharpness for maturity and roadmap (charts with text) to keep edges crisp
                  const preserveSharpness = slide.slideType === 'maturity' || slide.slideType === 'roadmap';
                  const resizedImageData = await resizeImageToDimensions(
                    slide.image,
                    finalWidth,
                    finalHeight,
                    preserveSharpness
                  );
                  const buffer = imageToBuffer(resizedImageData);
                  
                  imageMap.set(imageKey, {
                    buffer,
                    width: finalWidth,
                    height: finalHeight
                  });
                  
                  console.log(`Image stored with key: ${imageKey}`, {
                    original: `${originalWidth}x${originalHeight}`,
                    final: `${finalWidth}x${finalHeight}`,
                    aspectRatio: aspectRatio.toFixed(3),
                    bufferSize: buffer.length,
                    slideType: slide.slideType
                  });
                } catch (error) {
                  console.error(`Error processing image for slide ${slideIndex}:`, error);
                }
              }
            });
            
            await Promise.all(imagePromises);
          };
          
          // Process all images before continuing
          await processImages();
          
          // Configure image module
          const imageModuleOpts = {
            centered: false,
            fileType: 'pptx' as const,
            getImage: (tagValue: string) => {
              // tagValue is the placeholder name (e.g., 'project_output_image')
              const imageData = imageMap.get(tagValue);
              if (!imageData) {
                console.warn(`Image not found for tag: ${tagValue}`);
                return new Uint8Array(0);
              }
              return imageData.buffer;
            },
            getSize: (tagValue: string): [number, number] => {
              // Return actual dimensions from imageMap for accurate sizing
              // This ensures PowerPoint uses the correct dimensions for each image
              const imageData = imageMap.get(tagValue);
              if (imageData) {
                // Convert pixels to EMU (English Metric Units) used by PowerPoint
                // 1 pixel = 9525 EMU at 96 DPI
                const pixelsToEMU = 9525;
                return [
                  imageData.width * pixelsToEMU,
                  imageData.height * pixelsToEMU
                ] as [number, number];
              }
              // Fallback to default dimensions if image not found
              return [1125, 475] as [number, number];
            }
          };
          
          const imageModule = new ImageModule(imageModuleOpts);
          
          // Initialize Docxtemplater
          const doc = new Docxtemplater(zip, {
            paragraphLoop: true,
            linebreaks: true,
            parser: expressionParser,
            delimiters: {
              start: '{',
              end: '}'
            },
            modules: [imageModule]
          });

          // Build comprehensive render data object with all possible placeholder variations
          // This ensures we cover all possible placeholder names in the template
          // Initialize with formatted text for company profile and relationship summary
          
          // Extract relationship data with proper type checking
          const relationshipSummary = customerData?.customer_details?.relationship_summary || 'No relationship summary available.';
          const companyProfile = customerData?.customer_details?.company_profile || 'No company profile available.';
          
          // Debug: Log relationship data to verify it's being extracted
          console.log('Relationship data check:', {
            hasCustomerData: !!customerData,
            hasDetails: !!customerData?.customer_details,
            relationshipSummary: relationshipSummary,
            rawValue: customerData?.customer_details?.relationship_summary,
            companyProfile: companyProfile
          });
          
          const renderData: any = {
            // Client/Customer info - multiple variations
            client_name: customerName || 'Customer',
            customer_name: customerName || 'Customer',
            company_name: customerData?.customer_details?.name || 'Company Name',
            customerName: customerName || 'Customer',
            
            // Customer Overview data - formatted text (PowerPoint will handle wrapping)
            customer_overview_content: '',
            company_profile: formatTextForPPT(companyProfile),
            
            // Relationship summary - multiple placeholder name variations
            relationship_summary: formatTextForPPT(relationshipSummary),
            our_relationship: formatTextForPPT(relationshipSummary),
            relationship: formatTextForPPT(relationshipSummary),
            relationship_summary_text: formatTextForPPT(relationshipSummary),
            our_relationship_summary: formatTextForPPT(relationshipSummary),
            
            engagement_type: customerData?.customer_details?.industry_type || 'Industry Type',
            
            // Goals, Priorities, Trends - as arrays for loop tags
            key_goals: [],
            priorities: [],
            industry_trends: [],
            
            // Project Output
            project_output_title: '',
            project_output_content: '',
            project_output_title_1: '',
            project_output_description_1: '',
            
            // Maturity Assessment (defaults so template never shows [bracketed] placeholders)
            maturity_title: 'Maturity Journey',
            maturity_content: '',
            maturity_plan_name: 'Maturity Journey',
            // Exact template placeholder names – sensible defaults when no maturity slide is processed
            maturity_journey_description: formatTextForPPT(
              'This maturity journey visualization shows the 28-step transformation path from network foundation to advanced AI-enabled operations. The visualization tracks progress across the transformation journey and highlights the path to achieving AI-powered network automation.'
            ),
            maturity_journey_type: 'Maturity Journey',
            maturity_journey_image: '',
            maturity_journey_description_1: '',
            maturity_journey_type_1: 'Maturity Journey',
            maturity_journey_image_1: '',
            
            // Roadmap
            roadmap_title: '',
            transformation_roadmap_title: ''
          };
          
          // Process each slide and populate data
          for (let i = 0; i < slideData.length; i++) {
            const slide = slideData[i];
            
            if (slide.slideType === 'overview') {
              // Customer Overview - populate all overview fields
              renderData.customer_overview_content = slide.content || '';
              
              // Company profile and relationship summary are already formatted in initial renderData
              // But update them here in case they need to be refreshed with all variations
              renderData.company_profile = formatTextForPPT(companyProfile);
              
              // Update all relationship placeholder variations
              const formattedRelationship = formatTextForPPT(relationshipSummary);
              renderData.relationship_summary = formattedRelationship;
              renderData.our_relationship = formattedRelationship;
              renderData.relationship = formattedRelationship;
              renderData.relationship_summary_text = formattedRelationship;
              renderData.our_relationship_summary = formattedRelationship;
              
              // Extract goals, priorities, trends from customerData and format as arrays for loop tags
              // Template expects: {#key_goals}{goals}{/key_goals}
              const keyGoalsData = customerData?.goals?.key_goals?.slice(0, 3) || [];
              const prioritiesData = customerData?.goals?.priority_goals?.slice(0, 3) || [];
              const trendsData = customerData?.goals?.trend_goals?.slice(0, 3) || [];
              
              // Format as arrays of objects for docxtemplater loop tags
              // Each object should have the property name that matches the template placeholder
              renderData.key_goals = keyGoalsData.length > 0
                ? keyGoalsData.map((g: any) => ({
                    goals: formatTextForPPT(g.goal || '')
                  })).filter((item: any) => item.goals && item.goals.trim() !== '')
                : [{ goals: 'No key goals available.' }];
              
              renderData.priorities = prioritiesData.length > 0
                ? prioritiesData.map((p: any) => ({
                    priority: formatTextForPPT(p.name || '')
                  })).filter((item: any) => item.priority && item.priority.trim() !== '')
                : [{ priority: 'No priorities available.' }];
              
              renderData.industry_trends = trendsData.length > 0
                ? trendsData.map((t: any) => ({
                    trend: formatTextForPPT(t.TrendHighlights || '')
                  })).filter((item: any) => item.trend && item.trend.trim() !== '')
                : [{ trend: 'No industry trends available.' }];
              
              // Debug: Log the arrays to verify structure
              console.log('Goals, Priorities, Trends arrays:', {
                key_goals: renderData.key_goals,
                priorities: renderData.priorities,
                industry_trends: renderData.industry_trends
              });
              
            } else if (slide.slideType === 'project_output') {
              // Project Output - populate indexed placeholders for each slide
              const projIdx = slide.projectOutputIndex ?? 0;
              const titleKey = projIdx === 0 ? 'project_output_title' : 'project_output_title_1';
              const descKey = projIdx === 0 ? 'project_output_description' : 'project_output_description_1';
              const imageKey = projIdx === 0 ? 'project_output_image' : 'project_output_image_1';
              
              renderData[titleKey] = slide.title || 'Project Output';
              const projectDescription = slide.content || 
                `This Sankey diagram shows ${customerName}'s internal systems. It maps how legacy systems support departments, modernized through cloud platforms and AI, impacting efficiency and innovation.`;
              renderData[descKey] = formatTextForPPT(projectDescription);
              
              if (projIdx === 0) {
                renderData.project_output_content = formatTextForPPT(projectDescription);
                renderData.project_output_description = formatTextForPPT(projectDescription);
                renderData.project_description = formatTextForPPT(projectDescription);
              }
              
              if (slide.image && slide.image.length > 0 && imageMap.has(imageKey)) {
                renderData[imageKey] = imageKey;
              }
              
            } else if (slide.slideType === 'maturity') {
              // Maturity Assessment - populate indexed placeholders for each slide
              const matIdx = slide.maturitySlideIndex ?? 0;
              const descKey = matIdx === 0 ? 'maturity_journey_description' : 'maturity_journey_description_1';
              const typeKey = matIdx === 0 ? 'maturity_journey_type' : 'maturity_journey_type_1';
              const imageKey = matIdx === 0 ? 'maturity_journey_image' : 'maturity_journey_image_1';
              
              const maturityContent = slide.content || 
                'This maturity journey visualization shows the 28-step transformation path from network foundation to advanced AI-enabled operations.';
              const maturityPlanName = slide.maturityPlanName || '';
              
              renderData[descKey] = formatTextForPPT(maturityContent);
              renderData[typeKey] = maturityPlanName || 'Maturity Journey';
              
              if (slide.image && slide.image.length > 0 && imageMap.has(imageKey)) {
                renderData[imageKey] = imageKey;
              }
              
              if (matIdx === 0) {
                renderData.maturity_title = slide.title || 'Maturity Journey';
                renderData.maturity_content = formatTextForPPT(maturityContent);
                renderData.maturity_description = formatTextForPPT(maturityContent);
                renderData.maturity_plan_name = maturityPlanName;
              }
              
            } else if (slide.slideType === 'roadmap') {
              // Transformation Roadmap
              renderData.roadmap_title = slide.title;
              renderData.transformation_roadmap_title = slide.title;
              
              // Set image placeholder if image exists
              if (slide.image && slide.image.length > 0 && imageMap.has('roadmap_image')) {
                renderData.roadmap_image = 'roadmap_image';
              }
            }
          }
          
          // Ensure all text fields have values (replace empty strings with placeholder text)
          // Exclude placeholders that may be empty for some slides – must never show [bracketed]
          const excludedPlaceholderKeys = [
            'maturity_journey_description', 'maturity_journey_type',
            'maturity_journey_description_1', 'maturity_journey_type_1',
            'project_output_title', 'project_output_title_1',
            'project_output_description_1'
          ];
          Object.keys(renderData).forEach(key => {
            if (
              typeof renderData[key] === 'string' &&
              renderData[key].trim() === '' &&
              !key.includes('image') &&
              !excludedPlaceholderKeys.includes(key)
            ) {
              renderData[key] = `[${key.replace(/_/g, ' ')}]`;
            }
          });
          
          // Debug: Log render data to help identify issues
          console.log('=== PPT Export Debug Info ===');
          console.log('Render data keys:', Object.keys(renderData));
          console.log('Render data values:', renderData);
          console.log('Image map keys:', Array.from(imageMap.keys()));
          console.log('Image map sizes:', Array.from(imageMap.entries()).map(([key, val]) => ({
            key,
            bufferSize: val.buffer.length,
            dimensions: `${val.width}x${val.height}`
          })));
          console.log('Total slides processed:', slideData.length);
          console.log('Slide data:', slideData.map(s => ({
            title: s.title,
            slideType: s.slideType,
            hasImage: !!s.image,
            imageLength: s.image?.length || 0,
            contentLength: s.content?.length || 0
          })));
          
          // Validate that we have data
          if (Object.keys(renderData).length === 1 && renderData.client_name) {
            console.warn('Warning: Only client_name found in render data. Template placeholders may not match.');
          }
          
          if (imageMap.size === 0) {
            console.warn('Warning: No images found in image map. Images may not render.');
          }
          
          // Render the template
          try {
            doc.render(renderData);
            console.log('Template rendered successfully');
          } catch (renderError: any) {
            console.error('Error during template render:', renderError);
            console.error('Render error properties:', renderError.properties);
            throw renderError;
          }
          
          // Generate the output
          const out = doc.getZip().generate({
            type: 'blob',
            mimeType: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
          });

          const fileName = `${customerName}_Presentation_${new Date().toISOString().split('T')[0]}.pptx`;
          saveAs(out, fileName);
          
          // Reset button state
          if (exportButton) {
            exportButton.disabled = false;
            exportButton.innerHTML = 'Export as PPT';
          }
          
          // Return to first slide
          setCurrentSlide(0);
          
        } catch (error: any) {
          console.error('Error generating PPTX:', error);
          console.error('Error stack:', error.stack);
          alert('Error generating PowerPoint file. Please check console for details. Error: ' + error.message);
          
          // Reset button state on error
          if (exportButton) {
            exportButton.disabled = false;
            exportButton.innerHTML = 'Export as PPT';
          }
          
          // Ensure header and footer are visible again on error
          const header = document.querySelector('.sticky.top-0') as HTMLElement;
          const footer = document.querySelector('.w-full.py-4.text-center.bg-gray-100') as HTMLElement;
          if (header) header.style.display = '';
          if (footer) footer.style.display = '';
        }
      });
      
    } catch (error: any) {
      console.error('Error exporting to PPT:', error);
      alert('Error exporting to PPT. Please try again. Error: ' + error.message);
      
      // Reset button state on error
      const exportButton = document.querySelector('[data-export-ppt]') as HTMLButtonElement;
      if (exportButton) {
        exportButton.disabled = false;
        exportButton.innerHTML = 'Export as PPT';
      }
      
      // Ensure header and footer are visible again on error
      const header = document.querySelector('.sticky.top-0') as HTMLElement;
      const footer = document.querySelector('.w-full.py-4.text-center.bg-gray-100') as HTMLElement;
      if (header) header.style.display = '';
      if (footer) footer.style.display = '';
    }
  };

  if (!isOpen) return null;

  return (
    <div ref={presentationRef} className="fixed inset-0 z-50 bg-gray-100 overflow-x-hidden flex flex-col">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <div className="w-full h-full" style={{
          backgroundImage: `radial-gradient(circle at 25% 25%, #e5e7eb 1px, transparent 1px),
                           radial-gradient(circle at 75% 25%, #d1d5db 1px, transparent 1px)`,
          backgroundSize: '50px 50px, 30px 30px',
          backgroundPosition: '0 0, 25px 25px'
        }} />
      </div>

      {/* Header */}
      <div className="sticky top-0 left-0 right-0 z-10 bg-gray-100 flex-shrink-0">
        <div className="flex items-center justify-between px-8 py-4">
          {/* Company Name - Centered */}
          <div className="flex-1"></div>
          <h1 className="text-3xl font-bold text-gray-900 text-center flex-1 min-w-0 truncate px-4">
            {customerData?.customer_details?.name || 'Customer'}
          </h1>
          <div className="flex-1 flex justify-end">
            {/* Action Buttons */}
            <div className="flex items-center space-x-3 flex-shrink-0">
              <button
                data-export-pdf
                onClick={exportAsPDF}
                className="inline-flex items-center px-4 py-2 bg-gray-600 text-white text-sm font-medium rounded-lg hover:bg-gray-700 transition-colors"
              >
                <DocumentArrowDownIcon className="w-4 h-4 mr-2" />
                Export PDF
              </button>
              <button
                data-export-ppt
                onClick={exportAsPPT}
                className="inline-flex items-center px-4 py-2 bg-gray-600 text-white text-sm font-medium rounded-lg hover:bg-gray-700 transition-colors"
              >
                <DocumentArrowDownIcon className="w-4 h-4 mr-2" />
                Export PPT
              </button>
              <button
                onClick={onClose}
                className="px-4 py-2 bg-gray-600 text-white text-sm font-medium rounded-lg hover:bg-gray-700 transition-colors"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Slide Content - flex-1 with min-height to ensure footer positioning */}
      <div className="flex-1 overflow-y-auto overflow-x-hidden pt-2 flex flex-col min-h-0">
        <div className="w-full flex-1 flex flex-col" style={{ minHeight: 'calc(100vh - 200px)' }}>
          <div className="flex-1">
            {slides[currentSlide].component}
          </div>
          {/* Footer - sticky at bottom when content is short, at end when content overflows */}
          <div className="w-full pb-4 text-center bg-gray-100 mt-auto flex-shrink-0">
            <div className="text-blue-600 font-semibold text-lg">PRESIDIO</div>
          </div>
        </div>
      </div>

      {/* Custom CSS to remove borders only in presentation slides */}
      <style>{`
        /* Maturity slides: no scroll, fit content including bottom x-axis boxes */
        .presentation-slide-maturity {
          overflow: hidden !important;
          max-height: calc(100vh - 6rem);
        }
        
        .presentation-slide .border,
        .presentation-slide [class*="border"] {
          border: none !important;
        }
        .presentation-slide .rounded,
        .presentation-slide [class*="rounded"] {
          border-radius: 0 !important;
        }
        
        /* Ensure SVG fits within container and content is properly aligned */
        .presentation-slide svg {
          max-height: none !important;
          height: auto !important;
          width: 100% !important;
          max-width: 100% !important;
          display: block !important;
        }
        /* Override: Download button icon must stay compact (not chart SVGs) */
        .presentation-slide [data-download-png] svg {
          width: 1rem !important;
          height: 1rem !important;
          max-width: 1rem !important;
          max-height: 1rem !important;
          display: inline-block !important;
        }
        
        /* Ensure SVG containers center the content */
        .presentation-slide .flex.justify-center {
          display: flex !important;
          justify-content: center !important;
          align-items: center !important;
        }
        
        /* Fix container overflow issues - allow containers to grow */
        .presentation-slide .overflow-hidden {
          overflow: visible !important;
        }
        
        /* Ensure containers respect width constraints */
        .presentation-slide > div {
          overflow-x: hidden !important;
          max-width: 100% !important;
        }
        
        /* Content inside max-w-7xl must fit */
        .presentation-slide .max-w-7xl {
          overflow-x: hidden !important;
          max-width: 100% !important;
        }
        
        .presentation-slide .max-w-7xl > div {
          overflow-x: hidden !important;
          max-width: 100% !important;
        }
        
        /* Exception: Allow roadmap container to scroll horizontally */
        .presentation-slide .max-w-7xl > div > div.overflow-x-auto {
          overflow-x: auto !important;
          max-width: none !important;
        }
        
        /* Ensure maturity graph fits within container */
        .presentation-slide .TransformationMaturityGraph {
          width: 100% !important;
          max-width: 100% !important;
          height: auto !important;
          overflow-x: hidden !important;
        }
        
        /* Ensure SVG content and all elements are visible */
        .presentation-slide svg {
          overflow: visible !important;
        }
        
        /* Ensure left side IT maturity boxes are visible */
        .presentation-slide .legend {
          overflow: visible !important;
        }
        
        /* Ensure bottom maturity stage cards are visible */
        .presentation-slide .x-axis-boxes-group {
          overflow: visible !important;
        }
        
        /* Ensure all text elements are visible */
        .presentation-slide text {
          overflow: visible !important;
        }
        
        /* Ensure all groups are visible */
        .presentation-slide g {
          overflow: visible !important;
        }
        
        /* Remove any max-width/height constraints */
        .presentation-slide * {
          max-width: none !important;
          max-height: none !important;
        }
        
        /* Content containers must fit within parent */
        .presentation-slide #maturity-journey-graph {
          overflow-x: hidden !important;
          width: 100% !important;
          max-width: 100% !important;
        }
        
        /* Exception: Allow roadmap chart container to overflow for horizontal scrolling */
        .presentation-slide .overflow-x-auto {
          overflow-x: auto !important;
          overflow-y: visible !important;
        }
        
        .presentation-slide .overflow-x-auto .transformation-roadmap-chart {
          overflow-x: auto !important;
          overflow-y: visible !important;
          max-width: none !important;
        }
        
        /* Ensure the maturity graph container respects boundaries */
        .presentation-slide .relative {
          position: relative !important;
        }
        
        /* Prevent content from going beyond container */
        .presentation-slide .absolute.inset-0 {
          position: absolute !important;
          top: 0 !important;
          right: 0 !important;
          bottom: 0 !important;
          left: 0 !important;
        }
        

        
        /* Center content properly */
        .presentation-slide .flex.items-center.justify-center {
          display: flex !important;
          align-items: center !important;
          justify-content: center !important;
        }
        
        /* Ensure text is fully visible */
        .presentation-slide text {
          text-overflow: unset !important;
          overflow: visible !important;
          white-space: normal !important;
        }
        
        /* Prevent text truncation */
        .presentation-slide .annotation-note-label {
          text-overflow: unset !important;
          overflow: visible !important;
          white-space: normal !important;
        }
        
        /* Preserve chart styling - ensure roadmap chart displays properly */
        .presentation-slide .TransformationRoadmapChart svg {
          border: none !important;
          border-radius: 0 !important;
        }
        
        /* Preserve project box styling in roadmap */
        .presentation-slide .TransformationRoadmapChart rect {
          border-radius: 6px !important;
          stroke-width: 1.5px !important;
        }
        
        /* Ensure chart text is visible and properly styled */
        .presentation-slide .TransformationRoadmapChart text {
          overflow: visible !important;
          text-overflow: unset !important;
          white-space: normal !important;
        }
        
        /* Preserve chart container styling - allow horizontal scrolling */
        .presentation-slide .TransformationRoadmapChart {
          border: none !important;
          border-radius: 0 !important;
          overflow-x: auto !important;
          overflow-y: visible !important;
          max-width: none !important;
          width: auto !important;
        }
        
        /* Allow roadmap chart container to scroll horizontally */
        .presentation-slide .transformation-roadmap-chart {
          overflow-x: auto !important;
          overflow-y: visible !important;
          max-width: none !important;
          width: 100% !important;
        }
        
        /* Ensure roadmap SVG can exceed container width for scrolling */
        .presentation-slide .transformation-roadmap-chart svg {
          max-width: none !important;
          width: auto !important;
          min-width: 100% !important;
        }
        
        /* Hide See All buttons in presentation but keep functionality */
        .presentation-slide .presentation-hide {
          display: none !important;
        }

        .arrow-scale-down {
          transform: scale(0.1);
        }
      `}</style>
    </div>
  );
};

export default CustomerDemoPresentation;
